import { useState } from "react";
import {
  Loader2,
  Users,
  Wifi,
  ChevronDown,
  ChevronRight,
  ChevronDownIcon,
  Clock,
  CalendarClock,
  ArrowDownUp,
  Smartphone,
  Globe,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusDot } from "@/components/status-dot";
import { useStats } from "@/hooks/use-stats";
import { useStatus } from "@/hooks/use-status";
import { formatBytes, formatDateTime, formatDuration } from "@/lib/api";
import type { ConnectedClientInfo, IpSessionStats, UserStats } from "@/types";

export function StatsPage() {
  const { data: stats, isLoading: statsLoading } = useStats();
  const { data: status } = useStatus();
  const [view, setView] = useState("users");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Statistics</h1>
        <p className="text-sm text-muted-foreground">
          RADIUS accounting summaries and currently connected clients.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardDescription>Connected clients</CardDescription>
            <CardTitle className="flex items-center gap-2 text-2xl sm:text-3xl">
              <Wifi className="h-5 w-5 text-muted-foreground" />
              {statsLoading ? "—" : stats?.connectedCount ?? 0}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {status && <StatusDot status={status.status} />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 pb-2">
            <CardDescription>Users with sessions</CardDescription>
            <CardTitle className="flex items-center gap-2 text-2xl sm:text-3xl">
              <Users className="h-5 w-5 text-muted-foreground" />
              {statsLoading ? "—" : stats?.userStats.length ?? 0}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <p className="text-xs text-muted-foreground sm:text-sm">Within the retention window.</p>
          </CardContent>
        </Card>
      </div>

      {statsLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !stats || stats.userStats.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-4xl mb-3">&#x1F4CA;</p>
            <p className="text-muted-foreground">No session data available.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Mobile: compact native select */}
          <div className="relative w-fit sm:hidden">
            <select
              value={view}
              onChange={(e) => setView(e.target.value)}
              className="h-9 appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Select view"
            >
              <option value="users">Per user</option>
              <option value="connected">Connected ({stats.connectedCount})</option>
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>

          {/* Desktop: inline tabs */}
          <div className="hidden sm:block">
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="inline-flex h-9 p-1">
                <TabsTrigger value="users" className="gap-1.5 text-xs">
                  <Users className="h-3.5 w-3.5" />
                  Per user
                </TabsTrigger>
                <TabsTrigger value="connected" className="gap-1.5 text-xs">
                  <Wifi className="h-3.5 w-3.5" />
                  Connected
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                    {stats.connectedCount}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {view === "users" && (
            <div className="space-y-3">
              {stats.userStats.map((user) => (
                <UserStatsCard key={user.userName} user={user} />
              ))}
            </div>
          )}

          {view === "connected" && <ConnectedClientsList clients={stats.connectedClients} />}
        </div>
      )}
    </div>
  );
}

function UserStatsCard({ user }: { user: UserStats }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base">{user.userName}</CardTitle>
            <CardDescription className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
              <span>{user.sessionCount} session{user.sessionCount !== 1 ? "s" : ""}</span>
              <span className="text-muted-foreground/60">·</span>
              <span>{formatBytes(user.totalBytes)} total</span>
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="h-8 gap-1 px-2 text-xs"
          >
            {expanded ? (
              <>
                Hide <ChevronDown className="h-3.5 w-3.5" />
              </>
            ) : (
              <>
                Details <ChevronRight className="h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-4 pt-0">
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <StatItem icon={CalendarClock} label="Last start" value={formatDateTime(user.lastSessionStart)} />
          <StatItem icon={Clock} label="Last stop" value={formatDateTime(user.lastSessionStop)} />
          <StatItem icon={ArrowDownUp} label="Duration" value={formatDuration(user.lastSessionDuration)} />
          <StatItem
            icon={Smartphone}
            label="Traffic"
            value={`${formatBytes(user.totalOutputBytes)} in / ${formatBytes(user.totalInputBytes)} out`}
          />
        </div>

        {expanded && user.ipBreakdown.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <p className="mb-3 text-sm font-medium">Per-IP breakdown</p>
            <div className="space-y-2 md:hidden">
              {user.ipBreakdown.map((ip) => (
                <IpBreakdownCard key={ip.ipAddress} ip={ip} />
              ))}
            </div>
            <div className="hidden rounded-lg border md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client IP</TableHead>
                    <TableHead>VPN IP</TableHead>
                    <TableHead>Sessions</TableHead>
                    <TableHead>Traffic</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {user.ipBreakdown.map((ip) => (
                    <IpBreakdownRow key={ip.ipAddress} ip={ip} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-0.5 flex items-center gap-1 text-xs text-muted-foreground">
        <Icon className="h-3 w-3 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <p className="truncate text-sm font-medium">{value}</p>
    </div>
  );
}

function IpBreakdownCard({ ip }: { ip: IpSessionStats }) {
  return (
    <div className="rounded-lg border bg-card p-3 text-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate font-medium">{ip.ipAddress}</span>
        </div>
        <Badge variant="outline" className="h-5 px-1.5 text-[10px] shrink-0">
          {ip.sessionCount} session{ip.sessionCount !== 1 ? "s" : ""}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-muted-foreground">VPN IP:</span>{" "}
          <span className="font-medium">{ip.vpnIpAddress ?? "—"}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Traffic:</span>{" "}
          <span className="font-medium">{formatBytes(ip.totalBytes)}</span>
        </div>
      </div>
    </div>
  );
}

function IpBreakdownRow({ ip }: { ip: IpSessionStats }) {
  return (
    <TableRow>
      <TableCell className="font-medium">{ip.ipAddress}</TableCell>
      <TableCell>{ip.vpnIpAddress ?? "—"}</TableCell>
      <TableCell>{ip.sessionCount}</TableCell>
      <TableCell>{formatBytes(ip.totalBytes)}</TableCell>
    </TableRow>
  );
}

function ConnectedClientsList({ clients }: { clients: ConnectedClientInfo[] }) {
  if (clients.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No connected clients right now.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-2 md:hidden">
        {clients.map((client, idx) => (
          <Card key={`${client.userName}-${client.clientIp}-${idx}`}>
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="truncate font-semibold">{client.userName}</span>
                <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px]">
                  {client.clientIp}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">VPN IP:</span>{" "}
                  <span className="font-medium">{client.vpnIpAddress ?? "—"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Since:</span>{" "}
                  <span className="font-medium">{formatDateTime(client.sessionStart)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="hidden rounded-lg border bg-card md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Client IP</TableHead>
              <TableHead>VPN IP</TableHead>
              <TableHead>Session start</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.map((client, idx) => (
              <TableRow key={`${client.userName}-${client.clientIp}-${idx}`}>
                <TableCell className="font-medium">{client.userName}</TableCell>
                <TableCell>{client.clientIp}</TableCell>
                <TableCell>{client.vpnIpAddress ?? "—"}</TableCell>
                <TableCell>{formatDateTime(client.sessionStart)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
